import React, { useEffect, useRef, useState } from 'react';
import '../styles/VideoOverlay.css';
import ItemTooltip from './ItemTooltip';
import OverlayTeams from './OverlayTeams.jsx';

import useHeroCache from '../hooks/useHeroCache.js';
import useWeaponCache from '../hooks/useWeaponCache.js';
import useItemCache from '../hooks/useItemCache.js';
import useTwitchExtension from '../hooks/useTwitchExtension.js';
import useBroadcastChannel from '../hooks/useBroadcastChannel.js';
import useMatchSocket from '../hooks/useMatchSocket.js';
import useOverlayPlayers from '../hooks/useOverlayPlayers.js';
import useOverlayDebugTools from '../hooks/useOverlayDebugTools.js';

const VideoOverlay = ({ params }) => {
    const [activePlayers, setActivePlayers] = useState({});
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [gameActiveState, setGameActiveState] = useState(false);
    const [mouseActive, setMouseActive] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [hoveredItem, setHoveredItem] = useState(null);
    const [waitingForNextMatch, setWaitingForNextMatch] = useState(false);

    const { heroCache, loadHeroImage, loadingHeroes } = useHeroCache();
    const { weaponCache, loadWeaponData } = useWeaponCache();
    const { loadItemsForPlayer } = useItemCache();

    useEffect(() => {
        Object.values(heroCache).forEach(hero => {
            if (hero.weaponName && !weaponCache[hero.weaponName]) {
                loadWeaponData(hero.weaponName);
            }
        });
    }, [heroCache, weaponCache, loadWeaponData]);

    useEffect(() => {
        const hasActivePlayers = Object.keys(activePlayers).length > 0;
        const shouldShowOverlay = hasActivePlayers && gameActiveState && !waitingForNextMatch;

        if (shouldShowOverlay && !overlayVisible) {
            setOverlayVisible(true);
        } else if (!shouldShowOverlay && overlayVisible) {
            setOverlayVisible(false);
        }
    }, [activePlayers, gameActiveState, overlayVisible, waitingForNextMatch]);

    useEffect(() => {
        const handleMouseMove = () => setMouseActive(true);
        const handleMouseLeave = () => setMouseActive(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    const {
        globalPlayersRef,
        clearAllPortraits,
        handleGameEnd,
        handlePlayerCreate,
        handlePlayerUpdate,
        removePlayer
    } = useOverlayPlayers({
        loadHeroImage,
        loadItemsForPlayer,
        setActivePlayers,
        setOverlayVisible,
        setGameActiveState,
    });

    const { twitchId } = useTwitchExtension();

    const { sendMessage: sendInternalMessage } = useBroadcastChannel('circuit-overlay-communication', {
        onPlayerUpdate: (data) => handlePlayerUpdate(data),
        onPlayerCreate: (data) => handlePlayerCreate(data),
        onGameEnd: (data) => handleGameEnd(data),
        onClearAll: () => clearAllPortraits(),
    });

    const { waitingForNextMatchRef } = useMatchSocket({
        params,
        twitchId,
        globalPlayersRef,
        callbacks: {
            setActivePlayers,
            setWaitingForNextMatch,
            setGameActive: setGameActiveState,
            setOverlayVisible,
            clearAllPortraits,
            handleGameEnd,
            handlePlayerCreate,
            handlePlayerUpdate,
            sendInternalMessage,
            loadHeroImage,
            loadItemsForPlayer,
        },
    });

    useOverlayDebugTools({
        globalPlayersRef,
        waitingForNextMatchRef,
        setWaitingForNextMatch,
        clearAllPortraits,
        handlePlayerCreate,
        handlePlayerUpdate,
        removePlayer,
    });

    const containerRef = useRef(null);
    const containerOpacity = overlayVisible && mouseActive ? 1 : 0;

    return (
        <div className="video-overlay">
            <div
                ref={containerRef}
                className="container"
                style={{
                    opacity: containerOpacity,
                    transition: 'opacity 0.3s ease-in-out'
                }}
            >
                <div className="overlay-teams-container">
                    <OverlayTeams
                        activePlayers={activePlayers}
                        expandedRows={expandedRows}
                        setExpandedRows={setExpandedRows}
                        setHoveredItem={setHoveredItem}
                        loadingHeroes={loadingHeroes}
                    />
                </div>

                {hoveredItem && (
                    <ItemTooltip
                        item={hoveredItem.item}
                        x={hoveredItem.x}
                        y={hoveredItem.y}
                        heroId={hoveredItem.heroId}
                        heroCache={heroCache}
                        weaponCache={weaponCache}
                    />
                )}
            </div>
        </div>
    );
};

export default VideoOverlay;